import type { AdminSelf } from '@saay/api-client';
import { statusLabel } from '@saay/formatting';
import { Button, Icon } from '../kit.js';

import type { ActivationRequirement, AdminMerchant } from '../api.js';
import { canPerform, canUse } from '../permissions.js';

export type ActivationUiAction =
  | { readonly kind: 'capacity' }
  | { readonly kind: 'commercial' }
  | { readonly kind: 'edit' }
  | { readonly kind: 'kyc' }
  | { readonly kind: 'payout' }
  | { readonly kind: 'people-branch' }
  | { readonly kind: 'people-user' }
  | { readonly kind: 'plans' };

const ACTION_COPY: Readonly<
  Record<ActivationRequirement, { readonly action: ActivationUiAction; readonly label: string }>
> = {
  ACTIVE_BRANCH: { action: { kind: 'people-branch' }, label: 'افزودن شعبه' },
  CAPACITY: { action: { kind: 'capacity' }, label: 'تنظیم ظرفیت' },
  COMMERCIAL_SETTING: { action: { kind: 'commercial' }, label: 'ثبت کارمزد' },
  INSTALLMENT_PLAN: { action: { kind: 'plans' }, label: 'ثبت طرح' },
  KYC: { action: { kind: 'kyc' }, label: 'تأیید هویت' },
  MERCHANT_ADMIN: { action: { kind: 'people-user' }, label: 'افزودن مدیر' },
  PRIMARY_CATEGORY: { action: { kind: 'edit' }, label: 'ویرایش مشخصات' },
  VERIFIED_PAYOUT_ACCOUNT: { action: { kind: 'payout' }, label: 'حساب تسویه' },
};

function canRunAction(
  self: AdminSelf,
  merchant: AdminMerchant,
  action: ActivationUiAction,
): boolean {
  switch (action.kind) {
    case 'capacity':
      return canUse(self, 'requestCapacityAdjustment');
    case 'commercial':
      return canUse(self, 'createCommercialSettingVersion');
    case 'edit':
      return canUse(self, 'updateMerchant');
    case 'kyc':
      return (
        merchant.status === 'PENDING_ACTIVATION' &&
        merchant.kycVerifiedAt === null &&
        canUse(self, 'verifyMerchantKyc')
      );
    case 'payout':
      return canUse(self, 'listPayoutAccounts');
    case 'people-branch':
      return canPerform(self, 'createMerchantBranch');
    case 'people-user':
      return canPerform(self, 'createAdminMerchantUser');
    case 'plans':
      return canPerform(self, 'replaceInstallmentPlanActivations');
    default:
      return false;
  }
}

/**
 * The payout account is already entered (for example by the create wizard) and waits for the
 * ownership check (AC §18.5 P7): the line asks to verify it, not to enter it again.
 */
function payoutAwaitsVerification(merchant: AdminMerchant): boolean {
  return merchant.payoutAccount?.verificationStatus === 'PENDING';
}

/** A missing prerequisite reads as a checklist line, not as a status chip (W4). */
function RequirementLabel({ item, text }: { readonly item: string; readonly text?: string }) {
  return (
    <span className="admin-readiness-list__label">
      <Icon name="alert" />
      {text ?? statusLabel('activationRequirement', item)}
    </span>
  );
}

/** Each activation prerequisite with a direct action (ADM-16). */
export function ActivationReadinessList({
  merchant,
  onAction,
  self,
}: {
  readonly merchant: AdminMerchant;
  readonly onAction: (action: ActivationUiAction) => void;
  readonly self: AdminSelf;
}) {
  const { missing, ready } = merchant.activationReadiness;
  if (ready) {
    return <p className="admin-muted">همه پیش‌نیازها کامل است؛ فروشگاه آمادهٔ فعال‌سازی است.</p>;
  }
  return (
    <ul aria-label="آمادگی فعال‌سازی" className="admin-readiness-list">
      {missing.map((item) => {
        const copy = ACTION_COPY[item];
        if (copy === undefined) {
          return (
            <li className="admin-readiness-list__item" key={item}>
              <RequirementLabel item={item} />
            </li>
          );
        }
        const allowed = canRunAction(self, merchant, copy.action);
        const verifyPayout =
          item === 'VERIFIED_PAYOUT_ACCOUNT' && payoutAwaitsVerification(merchant);
        return (
          <li className="admin-readiness-list__item" key={item}>
            <RequirementLabel
              item={item}
              {...(verifyPayout ? { text: 'بررسی مالکیت حساب تسویهٔ ثبت‌شده' } : {})}
            />
            {allowed ? (
              <Button onClick={() => onAction(copy.action)} size="sm" variant="secondary">
                {verifyPayout ? 'بررسی مالکیت' : copy.label}
              </Button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
